/**
 * The lead desk's view logic, pure: grouping cards into the pipeline, moving
 * one optimistically, the WhatsApp link and email body for an edited draft,
 * and the influence page's periods. Tested without a browser.
 */
import type { DeskCard, LeadDeskStage, LeadTier } from '../../../common/hooks/use-lead-desk-api.hook';
import { LEAD_DESK_STAGES } from '../../../common/hooks/use-lead-desk-api.hook';
import type { StatusTone } from '../../../components/design-system/admin';

export const TIER_TONE: Record<LeadTier, StatusTone> = { hot: 'danger', warm: 'warning', cold: 'neutral' };

/** Stages a salesperson works, then the two endings. */
export const OPEN_STAGES: LeadDeskStage[] = ['new', 'contacted', 'sample', 'visit', 'quote'];

export interface StageColumn {
    stage: LeadDeskStage;
    cards: DeskCard[];
    /** Sum of the cards' values in the column's main currency; null when none has one. */
    value: { amount: number; currency: string } | null;
}

/** One column per stage, in pipeline order, each keeping the API's heat order. */
export function columnsOf(cards: DeskCard[]): StageColumn[] {
    return LEAD_DESK_STAGES.map((stage) => {
        const inStage = cards.filter((c) => c.stage === stage);
        const counts = new Map<string, number>();
        for (const c of inStage) if (c.value) counts.set(c.value.currency, (counts.get(c.value.currency) ?? 0) + 1);
        const currency = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
        const value = currency
            ? { amount: Math.round(inStage.filter((c) => c.value?.currency === currency).reduce((s, c) => s + (c.value?.amount ?? 0), 0)), currency }
            : null;
        return { stage, cards: inStage, value };
    });
}

/** The board after a card was dropped on another stage — before the API has answered. */
export function moveCard(cards: DeskCard[], id: string, stage: LeadDeskStage, now: Date = new Date()): DeskCard[] {
    return cards.map((c) => (c.id === id && c.stage !== stage ? { ...c, stage, stageAt: now.toISOString() } : c));
}

/** A display name: what they told us, else the part of the address before the @. */
export function displayName(card: { name?: string | null; email: string }): string {
    if (card.name && card.name.trim()) return card.name.trim();
    return card.email.split('@')[0];
}

export function initialOf(card: { name?: string | null; email: string }): string {
    return displayName(card).charAt(0).toUpperCase() || '?';
}

/**
 * The WhatsApp link for the text as the salesperson left it. The API resolved
 * the number (with the country code) for its own draft; that number is kept
 * and only the text follows the edit.
 */
export function whatsappLinkFor(apiLink: string | null | undefined, text: string): string {
    const digits = String(apiLink ?? '').match(/^https:\/\/wa\.me\/(\d*)/)?.[1] ?? '';
    return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * The email body for the text as edited: paragraphs, clickable links, the
 * render above them — the same shape the API gives its own draft.
 */
export function emailHtmlFor(text: string, opts: { renderImageUrl?: string | null; rtl?: boolean } = {}): string {
    const linkify = (s: string) =>
        escapeHtml(s).replace(/https?:\/\/[^\s<]+/g, (match) => {
            const url = match.replace(/[.,;:!?)]+$/, '');
            return `<a href="${url}">${url}</a>${match.slice(url.length)}`;
        });
    const paragraphs = text
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => `<p style="margin:0 0 14px;line-height:1.55">${linkify(p).replace(/\n/g, '<br>')}</p>`)
        .join('');
    const image = opts.renderImageUrl
        ? `<p style="margin:0 0 18px"><img src="${escapeHtml(opts.renderImageUrl)}" alt="" style="max-width:100%;border-radius:10px"></p>`
        : '';
    return `<div${opts.rtl ? ' dir="rtl"' : ''} style="font-family:Arial,Helvetica,sans-serif;font-size:15px;color:#1f1f1f">${image}${paragraphs}</div>`;
}

export type InfluencePeriod = '3m' | '6m' | '12m';
export const INFLUENCE_PERIODS: InfluencePeriod[] = ['3m', '6m', '12m'];

/** From the first day of the month N-1 months back, to today (UTC, YYYY-MM-DD). */
export function influenceRange(period: InfluencePeriod, now: Date = new Date()): { from: string; to: string } {
    const months = period === '3m' ? 3 : period === '12m' ? 12 : 6;
    const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (months - 1), 1));
    return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

/** "N×", rounded to what a person would say: one decimal under 10, whole numbers above. */
export function roiLabel(roi: number | null | undefined): string | null {
    if (roi === null || roi === undefined || !Number.isFinite(roi)) return null;
    return roi >= 10 ? `${Math.round(roi)}×` : `${Math.round(roi * 10) / 10}×`;
}

/** Days from now until `at`, negative when past. */
export function daysUntil(at: string | null | undefined, now: Date = new Date()): number | null {
    if (!at) return null;
    const t = new Date(at).getTime();
    return Number.isFinite(t) ? Math.round((t - now.getTime()) / 86_400_000) : null;
}
